/**
 * Cards-view selection modifiers. Plain click opens the card; Ctrl/Cmd
 * (Windows Ctrl, Mac Control or Command) toggles; Shift ranges from the
 * last anchor. Control+click on Mac also fires `contextmenu` — debounce
 * so the same gesture does not toggle twice.
 */

export const isAdditiveModifier = (event) => Boolean(event?.ctrlKey || event?.metaKey);

export const isRangeModifier = (event) =>
    Boolean(event?.shiftKey) && !isAdditiveModifier(event);

export const selectKeyRange = (orderedKeys, anchorKey, toKey) => {
    if (toKey == null || toKey === '') return [];
    if (!orderedKeys?.length) return [toKey];
    const end = orderedKeys.indexOf(toKey);
    if (end === -1) return [toKey];
    const start = orderedKeys.indexOf(anchorKey);
    if (start === -1) return [toKey];
    const from = Math.min(start, end);
    const to = Math.max(start, end);
    return orderedKeys.slice(from, to + 1);
};

const recentAdditive = { at: 0, key: null };

export const runAdditiveToggle = (key, toggleFn) => {
    const now = Date.now();
    if (key === recentAdditive.key && now - recentAdditive.at < 80) return;
    recentAdditive.at = now;
    recentAdditive.key = key;
    toggleFn?.();
};

/**
 * @returns {boolean} true when the click was consumed (do not navigate).
 */
export const handleCardSelectionClick = (event, {
    selectionKey,
    shouldSuppressClick,
    onToggleSelect,
    onRangeSelect,
} = {}) => {
    if (shouldSuppressClick?.()) return true;
    if (isAdditiveModifier(event) && onToggleSelect) {
        event.preventDefault?.();
        runAdditiveToggle(selectionKey, onToggleSelect);
        return true;
    }
    if (isRangeModifier(event) && onRangeSelect) {
        event.preventDefault?.();
        onRangeSelect();
        return true;
    }
    return false;
};

export const handleCardSelectionContextMenu = (event, { selectionKey, onToggleSelect } = {}) => {
    if (!onToggleSelect || !isAdditiveModifier(event)) return false;
    event.preventDefault?.();
    runAdditiveToggle(selectionKey, onToggleSelect);
    return true;
};

/** Keep a multi-select when the right-clicked card is already selected. */
export const applyContextMenuSelection = (prevKeys, key) => {
    if (key == null || key === '') return prevKeys || [];
    const current = prevKeys || [];
    return current.includes(key) ? current : [key];
};

/**
 * Right-click on a card: Control/Command still toggles selection (Mac).
 * Otherwise prevent the browser menu so a custom menu can open.
 * @returns {'additive' | 'menu'}
 */
export const handleCardContextMenu = (event, { selectionKey, onToggleSelect } = {}) => {
    if (handleCardSelectionContextMenu(event, { selectionKey, onToggleSelect })) {
        return 'additive';
    }
    event.preventDefault?.();
    return 'menu';
};
